import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Upload, Info } from "@phosphor-icons/react";
import { BugPriority, ItemType } from "shared/types/bug-report";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { ClerkUser } from "shared/types/user";
import { PrioritySelector } from "./PrioritySelector";
import { useUsers } from "shared/contexts/users.context";
import { DatePickerInput } from "shared/components/DatePickerInput/DatePickerInput";
import { getAllEpics } from "shared/services/epicService";
import { Epic, EpicStatus } from "shared/types/epic";

interface AddBugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedEpicId?: number;
}

// Predefined module tags (alphabetically sorted)
const PREDEFINED_TAGS = [
  'Acquisitie',
  'Algemeen',
  'Bedrijven',
  'Bestanden',
  'Contacten',
  'Corporate',
  'Documentgenerator',
  'E-mail module',
  'Ella AI',
  'Financieel',
  "Foto's",
  'Gebouwen',
  'Helpdesk',
  'Huurders communicatie',
  'Indexatie',
  'Leveranciers communicatie',
  'Renovatie',
  'Taken',
  'Verhuur',
  'Verzekeringen',
  'WhatsApp',
  'Zoekbalk',
];

export const AddBugReportModal: React.FC<AddBugReportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedEpicId
}) => {
  const { session } = useSession();
  const { user: currentUser } = useUser();
  const { users } = useUsers();
  const [epics, setEpics] = useState<Epic[]>([]);
  const [selectedEpic, setSelectedEpic] = useState<number | null>(preselectedEpicId || null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ItemType>(ItemType.BUG);
  const [priority, setPriority] = useState<BugPriority>(BugPriority.MEDIUM);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pastedImages, setPastedImages] = useState<{ url: string; file: File }[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<ClerkUser | null>(null);
  const [selectedReporter, setSelectedReporter] = useState<ClerkUser | null>(null);
  const [occurredAt, setOccurredAt] = useState('');
  const [pageUrl, setPageUrl] = useState('');

  // Fetch epics when modal opens
  useEffect(() => {
    if (!isOpen || !session) return;

    const fetchEpics = async () => {
      try {
        const data = await getAllEpics(session);
        setEpics(data);
      } catch (error) {
        console.error('Error fetching epics:', error);
        toast.error('Fout bij laden van epics');
      }
    };

    fetchEpics();
  }, [isOpen, session]);

  // Set default assignee based on environment
  useEffect(() => {
    if (!isOpen || !users || users.length === 0) return;

    // Detect if we're in production (Render deployment)
    const isProduction = API_URL?.includes('render.com') || API_URL?.includes('production');

    // Default user IDs
    const defaultUserId = isProduction
      ? 'user_2UD0QCwLgWuvorxfaRsQ365MbwB'  // Production
      : 'user_2Rs2ts9Oh55m080VXnubNKNlfo3'; // Development

    // Find the default user in the users list
    const defaultUser = users.find(user => user.id === defaultUserId);

    if (defaultUser && !selectedAssignee) {
      setSelectedAssignee(defaultUser);
    }
  }, [isOpen, users]);

  // Set preselected epic
  useEffect(() => {
    if (preselectedEpicId) {
      setSelectedEpic(preselectedEpicId);
    }
  }, [preselectedEpicId]);

  // Set current user as default reporter
  useEffect(() => {
    if (!isOpen || !currentUser || !users || users.length === 0) return;

    // Find current user in users list
    const currentUserData = users.find(u => u.id === currentUser.id);

    if (currentUserData && !selectedReporter) {
      setSelectedReporter(currentUserData);
    }
  }, [isOpen, currentUser, users]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          toast.error(`Bestand te groot: ${file.name} (max 10MB)`);
          return false;
        }
        if (!file.type.startsWith('image/')) {
          toast.error(`Alleen afbeeldingen toegestaan: ${file.name}`);
          return false;
        }
        return true;
      });
      setScreenshots([...screenshots, ...validFiles]);
    }
  };

  const handleRemoveScreenshot = (index: number) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          const file = new File([blob], `bug-screenshot-${Date.now()}.jpg`, {
            type: blob.type || "image/jpeg"
          });
          const url = URL.createObjectURL(file);

          // Voeg toe aan screenshots én aan paste preview
          setScreenshots(prev => [...prev, file]);
          setPastedImages(prev => [...prev, { url, file }]);
        }
      }
    }
  };

  const handleRemovePastedImage = (index: number) => {
    const imageToRemove = pastedImages[index];

    // Verwijder uit pastedImages
    setPastedImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Cleanup object URL
      URL.revokeObjectURL(imageToRemove.url);
      return updated;
    });

    // Verwijder ook uit screenshots
    setScreenshots(prev => prev.filter(s => s !== imageToRemove.file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Titel is verplicht');
      return;
    }

    if (!description.trim()) {
      toast.error('Beschrijving is verplicht');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('type', type);
      formData.append('priority', priority);
      tags.forEach((tag) => formData.append('tags', tag));

      // Add user agent and URL for debugging context
      formData.append('userAgent', navigator.userAgent);
      formData.append('url', window.location.href);

      // Add assignee if selected
      if (selectedAssignee) {
        formData.append('assignedTo', selectedAssignee.id?.toString() || '');
      }

      // Add reporter if selected
      if (selectedReporter) {
        formData.append('reportedBy', selectedReporter.id?.toString() || '');
      }

      // Add epic if selected
      if (selectedEpic) {
        formData.append('epicId', selectedEpic.toString());
      }

      // Add bug-specific fields
      if (type === ItemType.BUG) {
        if (occurredAt) {
          // Parse date and set time to noon to avoid timezone issues
          const dateObj = new Date(occurredAt);
          dateObj.setHours(12, 0, 0, 0);
          formData.append('occurredAt', dateObj.toISOString());
        }
        if (pageUrl) {
          formData.append('url', pageUrl);
        }
      }

      // Add screenshots
      screenshots.forEach((file, index) => {
        formData.append(`screenshot${index}`, file);
      });

      const response = await fetchWithAuth(
        `${API_URL}/bug-reports/create`,
        {
          method: 'POST',
          body: formData,
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to create bug report');
      }

      toast.success('Bug report succesvol aangemaakt!');
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating bug report:', error);
      toast.error(`Fout bij aanmaken bug report: ${error.message || 'Onbekende fout'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setType(ItemType.BUG);
    setPriority(BugPriority.MEDIUM);
    setTags([]);
    setTagInput('');
    setScreenshots([]);
    setSelectedAssignee(null);
    setSelectedReporter(null);
    setSelectedEpic(null);
    setOccurredAt('');
    setPageUrl('');

    // Cleanup object URLs
    pastedImages.forEach(img => URL.revokeObjectURL(img.url));
    setPastedImages([]);

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bug of Feature Melden</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Titel <span className="text-red-600">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Korte beschrijving"
              required
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select value={type} onValueChange={(value) => setType(value as ItemType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ItemType.BUG}>🐛 Bug</SelectItem>
                <SelectItem value={ItemType.FEATURE}>✨ Feature</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Prioriteit</Label>
            <div className="p-3 border rounded-md bg-neutral-50">
              <PrioritySelector
                priority={priority}
                onChange={setPriority}
              />
            </div>
          </div>

          {/* Epic Selection */}
          <div className="space-y-2">
            <Label htmlFor="epic">Epic (optioneel)</Label>
            <Select
              value={selectedEpic?.toString() || 'none'}
              onValueChange={(value) => setSelectedEpic(value === 'none' ? null : parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Geen epic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Geen epic</SelectItem>
                {epics
                  .sort((a, b) => {
                    // Sort by status priority first
                    const statusPriority = {
                      [EpicStatus.IN_PROGRESS]: 0,
                      [EpicStatus.PLANNING]: 1,
                      [EpicStatus.ON_HOLD]: 2,
                      [EpicStatus.COMPLETED]: 3
                    };
                    return statusPriority[a.status] - statusPriority[b.status];
                  })
                  .map((epic) => {
                    const statusEmoji = {
                      [EpicStatus.PLANNING]: '📋',
                      [EpicStatus.IN_PROGRESS]: '🚀',
                      [EpicStatus.COMPLETED]: '✅',
                      [EpicStatus.ON_HOLD]: '⏸️'
                    };
                    return (
                      <SelectItem key={epic.id} value={epic.id.toString()}>
                        {statusEmoji[epic.status]} {epic.title}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>

          {/* Reporter */}
          <div className="space-y-2">
            <Label htmlFor="reporter">Gemeld door</Label>
            <div className="flex items-center gap-2 p-2 border rounded-md">
              <UserSelection
                defaultUser={selectedReporter}
                onUserSelect={setSelectedReporter}
              />
              {selectedReporter && (
                <span className="text-sm text-neutral-600">
                  {selectedReporter.first_name} {selectedReporter.last_name}
                </span>
              )}
            </div>
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <Label htmlFor="assignee">Toegewezen aan</Label>
            <div className="flex items-center gap-2 p-2 border rounded-md">
              <UserSelection
                defaultUser={selectedAssignee}
                onUserSelect={setSelectedAssignee}
              />
              {selectedAssignee && (
                <span className="text-sm text-neutral-600">
                  {selectedAssignee.first_name} {selectedAssignee.last_name}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              {type === ItemType.FEATURE ? 'Wat is jouw suggestie om het proces te verbeteren?' : 'Beschrijving'} <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={type === ItemType.FEATURE
                ? "Beschrijf je suggestie zo gedetailleerd mogelijk..."
                : "Wat ging er mis? Welke stappen heb je genomen?"}
              rows={6}
              required
            />
          </div>

          {/* Bug-specific fields */}
          {type === ItemType.BUG && (
            <>
              {/* When did the bug occur */}
              <div className="space-y-2">
                <Label htmlFor="occurredAt">
                  Wanneer vond de bug plaats? (optioneel)
                </Label>
                <DatePickerInput
                  id="occurredAt"
                  value={occurredAt}
                  onChange={setOccurredAt}
                  placeholder="Kies een datum"
                />
                <p className="text-xs text-neutral-500">
                  Selecteer de datum waarop de bug zich voordeed
                </p>
              </div>

              {/* Page URL */}
              <div className="space-y-2">
                <Label htmlFor="pageUrl">
                  Op welke pagina vond de bug plaats? (optioneel)
                </Label>
                <Input
                  id="pageUrl"
                  type="url"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  placeholder="https://my.yally.app/..."
                />
                <p className="text-xs text-neutral-500 flex items-start gap-1">
                  <Info size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Kopieer de URL van de pagina waar de bug zich voordeed</span>
                </p>
              </div>

              {/* Console screenshot helper */}
              <div className="space-y-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Info size={16} />
                    Console screenshot toevoegen (optioneel)
                  </p>
                  <div className="text-xs text-blue-800 space-y-1">
                    <p>Een screenshot van de console kan helpen bij het debuggen:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Open de console: Druk op <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-900">F12</kbd> of <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-900">Ctrl+Shift+I</kbd> (Windows) / <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-900">Cmd+Option+I</kbd> (Mac)</li>
                      <li>Klik op het tabblad "Console"</li>
                      <li>Maak een screenshot van eventuele rode foutmeldingen</li>
                      <li>Voeg de screenshot toe onderaan dit formulier</li>
                    </ol>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Module/Tag</Label>

            {/* Predefined Tags Dropdown */}
            <div className="space-y-2">
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !tags.includes(value)) {
                    setTags([...tags, value]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer een module..." />
                </SelectTrigger>
                <SelectContent>
                  {PREDEFINED_TAGS
                    .filter(tag => !tags.includes(tag))
                    .map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Tag Input */}
            <div className="flex gap-2">
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Of voeg een eigen tag toe..."
              />
              <Button type="button" onClick={handleAddTag} variant="secondary">
                Toevoegen
              </Button>
            </div>

            {/* Selected Tags Display */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X
                      size={14}
                      className="cursor-pointer hover:text-red-600"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Screenshots */}
          <div className="space-y-2">
            <Label htmlFor="screenshots">Screenshots</Label>
            <div className="space-y-3">
              {/* Paste Area */}
              <div
                className="relative flex h-32 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 transition-colors hover:border-moss-primary hover:bg-moss-primary/5"
                onPaste={handlePaste}
                tabIndex={0}
                onClick={(e) => {
                  // Focus the element so paste works
                  (e.currentTarget as HTMLDivElement).focus();
                }}
              >
                <div className="flex flex-col items-center gap-2 text-center px-4">
                  <Upload size={24} className="text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-700">
                      Plak een screenshot (Ctrl/Cmd+V)
                    </p>
                    <p className="text-xs text-neutral-500">
                      of klik hieronder om bestanden te kiezen
                    </p>
                  </div>
                </div>
              </div>

              {/* File Upload Button */}
              <div className="flex items-center gap-2">
                <Input
                  id="screenshots"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('screenshots')?.click()}
                  className="w-full"
                >
                  <Upload size={16} className="mr-2" />
                  Kies bestanden
                </Button>
              </div>

              {/* Preview Grid */}
              {(screenshots.length > 0 || pastedImages.length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  {/* Show pasted images first */}
                  {pastedImages.map((pastedImg, index) => (
                    <div key={`pasted-${index}`} className="relative group">
                      <div className="aspect-video bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200">
                        <img
                          src={pastedImg.url}
                          alt={`Geplakte screenshot ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePastedImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={16} />
                      </button>
                      <div className="text-xs text-neutral-600 mt-1 truncate">
                        Geplakte screenshot
                      </div>
                    </div>
                  ))}

                  {/* Show uploaded files (excluding pasted ones) */}
                  {screenshots
                    .filter(file => !pastedImages.some(p => p.file === file))
                    .map((file, index) => (
                      <div key={`file-${index}`} className="relative group">
                        <div className="aspect-video bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const actualIndex = screenshots.findIndex(s => s === file);
                            handleRemoveScreenshot(actualIndex);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={16} />
                        </button>
                        <div className="text-xs text-neutral-600 mt-1 truncate">
                          {file.name}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Helper Text */}
              {screenshots.length === 0 && pastedImages.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <span className="rounded-sm bg-neutral-100 px-2 py-0.5">💡 Tip</span>
                  <span>Gebruik Ctrl/Cmd+V om snel een screenshot te plakken</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Bezig met verzenden...' : 'Melden'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

